import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CalendarDays,
  Check,
  ChevronDown,
  Clock3,
  Filter,
  Layers3,
  Loader2,
  MapPin,
  RefreshCw,
  Sprout,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { pageSubtitleCls, pageTitleCls, selectCls } from '../../components/ui';

type EstadoVisual = 'programada' | 'proceso' | 'aplicada' | 'cerrada' | 'cancelada';

interface FincaRow {
  id: string;
  nombre: string;
}

interface LoteRow {
  id: string;
  finca_id: string | null;
  numero: string;
  nombre: string | null;
  area_ha: number | null;
  activo: boolean | null;
}

interface BloqueRow {
  id: string;
  num: string | number;
  estadio_vfp: string | null;
  ciclo: string | null;
  plant_count: number | null;
  area_ha: number | null;
}

interface BoletaTimeline {
  id: string;
  idNumerico: number | null;
  origen: 'ERP' | 'VFP';
  numero: string;
  semana: string | null;
  estado: string;
  fechaPlan: string | null;
  fechaAplicacion: string | null;
  createdAt: string | null;
  despachadoAt: string | null;
  aplicadoAt: string | null;
  vobAt: string | null;
  etapa: string | null;
  ciclo: string | null;
  bloques: string | null;
  areaHa: number | null;
  plantas: number | null;
  boomsCompletos: number;
  litrosFraccion: number;
}

interface ProgresoBoom {
  total: number;
  despachados: number;
  iniciados: number;
  finalizados: number;
  ultimoFin: string | null;
}

interface MesResumen {
  key: string;
  label: string;
  total: number;
  valores: Record<EstadoVisual, number>;
}

const ESTADOS: Record<EstadoVisual, { label: string; color: string; bg: string; border: string }> = {
  programada: { label: 'Programada', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.38)' },
  proceso: { label: 'En proceso', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.38)' },
  aplicada: { label: 'Aplicada', color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.38)' },
  cerrada: { label: 'Con V°B°', color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.38)' },
  cancelada: { label: 'Cancelada', color: '#ef4444', bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.34)' },
};

const ORDEN_ESTADOS: EstadoVisual[] = ['programada', 'proceso', 'aplicada', 'cerrada', 'cancelada'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function estadoVisual(estado: string): EstadoVisual {
  const value = String(estado || '').toLowerCase();
  if (value === 'cancelada' || value === 'anulada') return 'cancelada';
  if (value === 'archivada') return 'cerrada';
  if (value === 'aplicada') return 'aplicada';
  if (value === 'en_proceso') return 'proceso';
  return 'programada';
}

function parseFecha(value?: string | null): Date | null {
  if (!value) return null;
  const soloFecha = /^\d{4}-\d{2}-\d{2}$/.test(value.slice(0, 10));
  const date = new Date(soloFecha ? `${value.slice(0, 10)}T12:00:00` : value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function fmtFecha(value?: string | null): string {
  const date = parseFecha(value);
  return date ? date.toLocaleDateString('es-CR', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Sin fecha';
}

function fechaEvento(boleta: BoletaTimeline, progreso?: ProgresoBoom): Date | null {
  return parseFecha(boleta.fechaAplicacion)
    || parseFecha(progreso?.ultimoFin)
    || parseFecha(boleta.fechaPlan)
    || parseFecha(boleta.createdAt);
}

function fechaIsoEvento(boleta: BoletaTimeline, progreso?: ProgresoBoom): string | null {
  return boleta.fechaAplicacion || progreso?.ultimoFin || boleta.fechaPlan || boleta.createdAt;
}

function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' });
}

function inicioRango(meses: number): Date | null {
  if (meses === 0) return null;
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setMonth(date.getMonth() - meses);
  return date;
}

function MonthlyChart({ meses }: { meses: MesResumen[] }) {
  const max = Math.max(1, ...meses.map((mes) => mes.total));

  if (meses.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-xs" style={{ color: 'var(--ink-faint)' }}>
        No hay aplicaciones con fecha en el período seleccionado.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto pb-1">
      <div className="flex h-52 items-end gap-2" style={{ minWidth: Math.max(620, meses.length * 58) }}>
        {meses.map((mes) => (
          <div key={mes.key} className="flex h-full flex-1 flex-col items-center justify-end" style={{ minWidth: 46 }}>
            <div className="mb-1 text-[10px] font-bold" style={{ color: 'var(--ink-muted)' }}>{mes.total}</div>
            <div
              className="flex w-full flex-col-reverse overflow-hidden rounded-t-md"
              style={{ height: Math.max(8, Math.round((mes.total / max) * 154)), background: 'var(--surface-overlay)' }}
              title={`${mes.label}: ${mes.total} aplicaciones`}
            >
              {ORDEN_ESTADOS.map((key) => {
                const value = mes.valores[key];
                if (!value) return null;
                return (
                  <div
                    key={key}
                    title={`${ESTADOS[key].label}: ${value}`}
                    style={{
                      height: `${(value / mes.total) * 100}%`,
                      minHeight: value > 0 ? 3 : 0,
                      background: ESTADOS[key].color,
                      borderTop: '1px solid rgba(255,255,255,0.24)',
                    }}
                  />
                );
              })}
            </div>
            <div className="mt-2 text-center text-[10px] capitalize" style={{ color: 'var(--ink-faint)' }}>{mes.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FlujoAplicacion({ boleta, progreso }: { boleta: BoletaTimeline; progreso?: ProgresoBoom }) {
  const estado = estadoVisual(boleta.estado);
  const cancelada = estado === 'cancelada';
  const tieneDespacho = !!boleta.despachadoAt || (progreso?.despachados ?? 0) > 0 || ['proceso', 'aplicada', 'cerrada'].includes(estado);
  const tieneCampo = (progreso?.iniciados ?? 0) > 0 || ['aplicada', 'cerrada'].includes(estado);
  const aplicada = ['aplicada', 'cerrada'].includes(estado);
  const cerrada = estado === 'cerrada';
  const pasos = [
    { label: 'Planificada', ok: !cancelada },
    { label: 'Despacho', ok: !cancelada && tieneDespacho },
    { label: 'Campo', ok: !cancelada && tieneCampo },
    { label: 'Aplicada', ok: !cancelada && aplicada },
    { label: 'V°B°', ok: !cancelada && cerrada },
  ];

  return (
    <div className="mt-4 grid grid-cols-5">
      {pasos.map((paso, index) => (
        <div key={paso.label} className="relative flex min-w-0 flex-col items-center">
          {index > 0 && (
            <div
              className="absolute top-[9px] right-1/2 h-0.5 w-full"
              style={{ background: paso.ok ? ESTADOS[estado].color : 'var(--line)' }}
            />
          )}
          <span
            className="relative z-[1] flex h-5 w-5 items-center justify-center rounded-full border text-[10px]"
            style={{
              background: paso.ok ? ESTADOS[estado].color : 'var(--surface-raised)',
              borderColor: paso.ok ? ESTADOS[estado].color : 'var(--line)',
              color: paso.ok ? '#fff' : 'var(--ink-faint)',
            }}
          >
            {paso.ok ? <Check size={11} strokeWidth={3} /> : index + 1}
          </span>
          <span className="mt-1 truncate text-[9px] sm:text-[10px]" style={{ color: paso.ok ? 'var(--ink-muted)' : 'var(--ink-faint)' }}>
            {paso.label}
          </span>
        </div>
      ))}
    </div>
  );
}

export function LineaTiempoLotePage() {
  const empresaId = useEmpresaId();
  const [fincas, setFincas] = useState<FincaRow[]>([]);
  const [lotes, setLotes] = useState<LoteRow[]>([]);
  const [fincaId, setFincaId] = useState('');
  const [loteId, setLoteId] = useState('');
  const [boletas, setBoletas] = useState<BoletaTimeline[]>([]);
  const [bloques, setBloques] = useState<BloqueRow[]>([]);
  const [progreso, setProgreso] = useState<Map<number, ProgresoBoom>>(new Map());
  const [rangoMeses, setRangoMeses] = useState(12);
  const [filtroEstado, setFiltroEstado] = useState<EstadoVisual | 'todos'>('todos');
  const [ordenAscendente, setOrdenAscendente] = useState(false);
  const [loadingCatalogo, setLoadingCatalogo] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    (async () => {
      setLoadingCatalogo(true);
      const [fincasRes, lotesRes] = await Promise.all([
        supabase.from('emp_fincas').select('id,nombre').eq('empresa_id', empresaId).eq('activo', true).order('nombre'),
        supabase.from('emp_lotes').select('id,finca_id,numero,nombre,area_ha,activo').eq('empresa_id', empresaId).eq('activo', true),
      ]);
      if (!vivo) return;
      setLoadingCatalogo(false);
      if (fincasRes.error || lotesRes.error) {
        setError(fincasRes.error?.message || lotesRes.error?.message || 'No se pudo cargar el catálogo de lotes.');
        return;
      }
      const nextFincas = (fincasRes.data ?? []) as FincaRow[];
      const nextLotes = ((lotesRes.data ?? []) as LoteRow[]).sort((a, b) => naturalCompare(a.numero, b.numero));
      setFincas(nextFincas);
      setLotes(nextLotes);
      if (nextLotes.length > 0) {
        setLoteId((actual) => actual || nextLotes[0].id);
        setFincaId((actual) => actual || nextLotes[0].finca_id || '');
      }
    })();
    return () => { vivo = false; };
  }, [empresaId]);

  const cargarDetalle = useCallback(async () => {
    if (!loteId) {
      setBoletas([]);
      setBloques([]);
      setProgreso(new Map());
      return;
    }
    const lote = lotes.find((item) => item.id === loteId);
    if (!lote) return;

    setLoading(true);
    setError('');
    const variantesLote = [...new Set([
      String(lote.numero || '').trim(),
      String(Number(lote.numero)),
      String(lote.numero || '').trim().padStart(2, '0'),
    ].filter(Boolean))];

    const [boletasRes, bloquesRes, vfpRes] = await Promise.all([
      supabase
        .from('app_boletas')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('lote_id', loteId)
        .order('fecha_plan', { ascending: false, nullsFirst: false })
        .limit(1000),
      supabase
        .from('emp_bloques')
        .select('id,num,estadio_vfp,ciclo,plant_count,area_ha')
        .eq('empresa_id', empresaId)
        .eq('lote_id', loteId)
        .eq('activo', true)
        .order('num'),
      variantesLote.length > 0
        ? supabase
          .from('app_vfp_cedula')
          .select('nboleta,semana,lote_num,finca,bloques,plantas,area_ha,fsiembra,faplica,fechaaplic,nestadio,ciclo,aplicado,cboom,cfraccion')
          .eq('empresa_id', empresaId)
          .in('lote_num', variantesLote)
          .order('faplica', { ascending: false })
          .limit(1000)
        : Promise.resolve({ data: [], error: null }),
    ]);

    if (boletasRes.error || bloquesRes.error) {
      setError(boletasRes.error?.message || bloquesRes.error?.message || 'No se pudo cargar la línea de tiempo.');
      setLoading(false);
      return;
    }

    const modernas: BoletaTimeline[] = (boletasRes.data ?? []).map((row: any) => ({
      id: `erp-${row.id}`,
      idNumerico: Number(row.id),
      origen: 'ERP',
      numero: String(row.numero ?? row.id),
      semana: row.semana ?? null,
      estado: String(row.estado ?? 'pendiente'),
      fechaPlan: row.fecha_plan ?? row.fecha_desde ?? null,
      fechaAplicacion: row.fecha_aplicacion ?? null,
      createdAt: row.created_at ?? null,
      despachadoAt: row.despachado_at ?? row.mezcla_preparada_at ?? null,
      aplicadoAt: row.aplicado_at ?? null,
      vobAt: row.vob_at ?? null,
      etapa: row.etapa_nombre ?? null,
      ciclo: row.ciclo_nombre ?? null,
      bloques: row.bloques ?? null,
      areaHa: row.area_ha == null ? null : Number(row.area_ha),
      plantas: row.plantas == null ? null : Number(row.plantas),
      boomsCompletos: Number(row.n_booms_completos || 0),
      litrosFraccion: Number(row.litros_fraccion || 0),
    }));

    const numerosModernos = new Set(modernas.map((row) => row.numero));
    const historicas: BoletaTimeline[] = vfpRes.error ? [] : (vfpRes.data ?? [])
      .filter((row: any) => !numerosModernos.has(String(row.nboleta)))
      .map((row: any) => ({
        id: `vfp-${row.nboleta}`,
        idNumerico: null,
        origen: 'VFP' as const,
        numero: String(row.nboleta ?? '—'),
        semana: row.semana ?? null,
        estado: row.aplicado ? 'archivada' : 'cancelada',
        fechaPlan: row.faplica ?? null,
        fechaAplicacion: row.fechaaplic ?? (row.aplicado ? row.faplica : null),
        createdAt: null,
        despachadoAt: row.aplicado ? row.fechaaplic ?? row.faplica : null,
        aplicadoAt: row.aplicado ? row.fechaaplic ?? row.faplica : null,
        vobAt: row.aplicado ? row.fechaaplic ?? row.faplica : null,
        etapa: row.nestadio ?? null,
        ciclo: row.ciclo ?? null,
        bloques: row.bloques ?? null,
        areaHa: row.area_ha == null ? null : Number(row.area_ha),
        plantas: row.plantas == null ? null : Number(row.plantas),
        boomsCompletos: Number(row.cboom || 0),
        litrosFraccion: Number(row.cfraccion || 0),
      }));

    const ids = modernas.map((row) => row.idNumerico).filter((id): id is number => id != null);
    const progressMap = new Map<number, ProgresoBoom>();
    const ensure = (id: number, boleta?: BoletaTimeline) => {
      let item = progressMap.get(id);
      if (!item) {
        const total = boleta ? boleta.boomsCompletos + (boleta.litrosFraccion > 0 ? 1 : 0) : 0;
        item = { total, despachados: 0, iniciados: 0, finalizados: 0, ultimoFin: null };
        progressMap.set(id, item);
      }
      return item;
    };
    modernas.forEach((row) => row.idNumerico != null && ensure(row.idNumerico, row));

    if (ids.length > 0) {
      const [despachosRes, aplicadosRes] = await Promise.all([
        supabase.from('app_boleta_booms_despachados').select('boleta_id,boom_num,estado').eq('empresa_id', empresaId).in('boleta_id', ids),
        supabase.from('app_boleta_booms_aplicados').select('boleta_id,boom_num,inicio_at,fin_at').eq('empresa_id', empresaId).in('boleta_id', ids),
      ]);
      (despachosRes.data ?? []).forEach((row: any) => {
        const item = ensure(Number(row.boleta_id));
        if (row.estado === 'despachado' || row.estado === 'descartado') item.despachados += 1;
        if (row.estado === 'descartado') item.finalizados += 1;
      });
      (aplicadosRes.data ?? []).forEach((row: any) => {
        const item = ensure(Number(row.boleta_id));
        if (row.inicio_at) item.iniciados += 1;
        if (row.fin_at) {
          item.finalizados += 1;
          if (!item.ultimoFin || row.fin_at > item.ultimoFin) item.ultimoFin = row.fin_at;
        }
      });
    }

    setBloques((bloquesRes.data ?? []) as BloqueRow[]);
    setBoletas([...modernas, ...historicas]);
    setProgreso(progressMap);
    setLoading(false);
  }, [empresaId, loteId, lotes]);

  useEffect(() => { cargarDetalle(); }, [cargarDetalle]);

  const lotesDeFinca = useMemo(
    () => lotes.filter((lote) => !fincaId || lote.finca_id === fincaId),
    [lotes, fincaId],
  );

  const loteSeleccionado = lotes.find((lote) => lote.id === loteId) ?? null;
  const fincaSeleccionada = fincas.find((finca) => finca.id === loteSeleccionado?.finca_id) ?? null;
  const desde = inicioRango(rangoMeses);

  const boletasEnRango = useMemo(() => boletas.filter((boleta) => {
    const date = fechaEvento(boleta, boleta.idNumerico == null ? undefined : progreso.get(boleta.idNumerico));
    return !desde || !date || date >= desde;
  }), [boletas, desde, progreso]);

  const conteos = useMemo(() => {
    const initial: Record<EstadoVisual, number> = { programada: 0, proceso: 0, aplicada: 0, cerrada: 0, cancelada: 0 };
    boletasEnRango.forEach((boleta) => { initial[estadoVisual(boleta.estado)] += 1; });
    return initial;
  }, [boletasEnRango]);

  const boletasVisibles = useMemo(() => boletasEnRango
    .filter((boleta) => filtroEstado === 'todos' || estadoVisual(boleta.estado) === filtroEstado)
    .sort((a, b) => {
      const pa = a.idNumerico == null ? undefined : progreso.get(a.idNumerico);
      const pb = b.idNumerico == null ? undefined : progreso.get(b.idNumerico);
      const ta = fechaEvento(a, pa)?.getTime() ?? 0;
      const tb = fechaEvento(b, pb)?.getTime() ?? 0;
      return ordenAscendente ? ta - tb : tb - ta;
    }), [boletasEnRango, filtroEstado, ordenAscendente, progreso]);

  const meses = useMemo(() => {
    const porMes = new Map<string, MesResumen>();
    boletasEnRango.forEach((boleta) => {
      const p = boleta.idNumerico == null ? undefined : progreso.get(boleta.idNumerico);
      const date = fechaEvento(boleta, p);
      if (!date) return;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      let mes = porMes.get(key);
      if (!mes) {
        mes = {
          key,
          label: `${MESES_CORTOS[date.getMonth()]} ${String(date.getFullYear()).slice(2)}`,
          total: 0,
          valores: { programada: 0, proceso: 0, aplicada: 0, cerrada: 0, cancelada: 0 },
        };
        porMes.set(key, mes);
      }
      mes.total += 1;
      mes.valores[estadoVisual(boleta.estado)] += 1;
    });
    return [...porMes.values()].sort((a, b) => a.key.localeCompare(b.key)).slice(-24);
  }, [boletasEnRango, progreso]);

  const estadiosActuales = useMemo(() => {
    const map = new Map<string, { codigo: string; ciclo: string; bloques: number; plantas: number; area: number }>();
    bloques.forEach((bloque) => {
      const codigo = bloque.estadio_vfp || 'Sin estadio';
      const ciclo = bloque.ciclo || '—';
      const key = `${codigo}|${ciclo}`;
      const item = map.get(key) || { codigo, ciclo, bloques: 0, plantas: 0, area: 0 };
      item.bloques += 1;
      item.plantas += Number(bloque.plant_count || 0);
      item.area += Number(bloque.area_ha || 0);
      map.set(key, item);
    });
    return [...map.values()].sort((a, b) => b.bloques - a.bloques);
  }, [bloques]);

  const totalActivo = Math.max(0, boletasEnRango.length - conteos.cancelada);
  const completadas = conteos.aplicada + conteos.cerrada;
  const avance = totalActivo > 0 ? Math.round((completadas / totalActivo) * 100) : 0;
  const totalEstados = Math.max(1, boletasEnRango.length);

  function cambiarFinca(value: string) {
    setFincaId(value);
    const opciones = lotes.filter((lote) => !value || lote.finca_id === value);
    if (!opciones.some((lote) => lote.id === loteId)) setLoteId(opciones[0]?.id || '');
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <CalendarDays size={20} style={{ color: 'var(--emp-accent-txt)' }} />
            <h1 className={pageTitleCls}>Línea de tiempo por lote</h1>
          </div>
          <p className={pageSubtitleCls}>Avance agronómico y operativo de las aplicaciones, desde la planificación hasta el V°B°.</p>
        </div>
        <button
          type="button"
          onClick={cargarDetalle}
          disabled={loading || !loteId}
          className="inline-flex items-center gap-2 rounded border px-3 py-2 text-xs font-semibold disabled:opacity-50"
          style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)', background: 'var(--surface-raised)' }}
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Actualizar
        </button>
      </div>

      <div className="grid gap-3 rounded-lg border p-4 md:grid-cols-3" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--ink-faint)' }}>Finca</label>
          <select className={selectCls} value={fincaId} onChange={(event) => cambiarFinca(event.target.value)} disabled={loadingCatalogo}>
            <option value="">Todas las fincas</option>
            {fincas.map((finca) => <option key={finca.id} value={finca.id}>{finca.nombre}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--ink-faint)' }}>Lote</label>
          <select className={selectCls} value={loteId} onChange={(event) => setLoteId(event.target.value)} disabled={loadingCatalogo || lotesDeFinca.length === 0}>
            {lotesDeFinca.length === 0 && <option value="">Sin lotes activos</option>}
            {lotesDeFinca.map((lote) => <option key={lote.id} value={lote.id}>Lote {lote.numero}{lote.nombre ? ` — ${lote.nombre}` : ''}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--ink-faint)' }}>Período</label>
          <select className={selectCls} value={rangoMeses} onChange={(event) => setRangoMeses(Number(event.target.value))}>
            <option value={3}>Últimos 3 meses</option>
            <option value={6}>Últimos 6 meses</option>
            <option value={12}>Últimos 12 meses</option>
            <option value={24}>Últimos 24 meses</option>
            <option value={0}>Todo el historial</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border px-4 py-3 text-xs" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
          <AlertCircle size={15} /> {error}
        </div>
      )}

      {loteSeleccionado && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border px-4 py-3" style={{ borderColor: 'var(--line)', background: 'var(--surface-deep)' }}>
          <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
            <MapPin size={15} style={{ color: 'var(--emp-accent-txt)' }} /> Lote {loteSeleccionado.numero}
          </div>
          <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{loteSeleccionado.nombre || 'Sin nombre'}</span>
          <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{fincaSeleccionada?.nombre || 'Finca sin identificar'}</span>
          {loteSeleccionado.area_ha != null && <span className="text-xs font-mono" style={{ color: 'var(--ink-faint)' }}>{Number(loteSeleccionado.area_ha).toFixed(2)} ha</span>}
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[360px] items-center justify-center gap-2 text-sm" style={{ color: 'var(--ink-faint)' }}>
          <Loader2 size={20} className="animate-spin" /> Construyendo línea de tiempo…
        </div>
      ) : loteSeleccionado ? (
        <>
          <div className="grid gap-4 lg:grid-cols-[0.8fr_1.2fr]">
            <div className="rounded-lg border p-4" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Avance de aplicaciones</p>
                  <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>Aplicadas o con V°B° sobre el total activo</p>
                </div>
                <div
                  className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full"
                  style={{ background: `conic-gradient(#22c55e ${avance}%, var(--surface-overlay) ${avance}% 100%)` }}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full text-lg font-bold" style={{ background: 'var(--surface-raised)', color: 'var(--ink)' }}>
                    {avance}%
                  </div>
                </div>
              </div>

              <div className="mb-3 flex h-3 overflow-hidden rounded-full" style={{ background: 'var(--surface-overlay)' }}>
                {ORDEN_ESTADOS.map((key) => conteos[key] > 0 && (
                  <div key={key} title={`${ESTADOS[key].label}: ${conteos[key]}`} style={{ width: `${(conteos[key] / totalEstados) * 100}%`, background: ESTADOS[key].color }} />
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {ORDEN_ESTADOS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFiltroEstado((actual) => actual === key ? 'todos' : key)}
                    className="rounded-md border px-2 py-2 text-left transition-opacity hover:opacity-80"
                    style={{
                      borderColor: filtroEstado === key ? ESTADOS[key].color : ESTADOS[key].border,
                      background: ESTADOS[key].bg,
                      outline: filtroEstado === key ? `1px solid ${ESTADOS[key].color}` : 'none',
                    }}
                  >
                    <span className="block text-lg font-bold" style={{ color: ESTADOS[key].color }}>{conteos[key]}</span>
                    <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{ESTADOS[key].label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg border p-4" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Aplicaciones por mes</p>
                  <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>Cada barra se divide según el estado actual de las boletas</p>
                </div>
                <div className="flex flex-wrap justify-end gap-x-3 gap-y-1">
                  {ORDEN_ESTADOS.map((key) => (
                    <span key={key} className="inline-flex items-center gap-1 text-[9px]" style={{ color: 'var(--ink-faint)' }}>
                      <i className="h-2 w-2 rounded-sm" style={{ background: ESTADOS[key].color }} /> {ESTADOS[key].label}
                    </span>
                  ))}
                </div>
              </div>
              <MonthlyChart meses={meses} />
            </div>
          </div>

          <div className="rounded-lg border p-4" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
            <div className="mb-3 flex items-center gap-2">
              <Sprout size={15} style={{ color: 'var(--tone-success-text)' }} />
              <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Estado agronómico actual</p>
              <span className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{bloques.length} bloques activos</span>
            </div>
            {estadiosActuales.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>El lote no tiene bloques activos con estadio registrado.</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                {estadiosActuales.map((item) => (
                  <div key={`${item.codigo}-${item.ciclo}`} className="rounded-md border px-3 py-2" style={{ borderColor: 'var(--line-dim)', background: 'var(--surface-overlay)' }}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-sm font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{item.codigo}</span>
                      <span className="rounded px-1.5 py-0.5 text-[9px] font-bold" style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)' }}>{item.ciclo}</span>
                    </div>
                    <p className="mt-1 text-[11px]" style={{ color: 'var(--ink-muted)' }}>{item.bloques} bloques · {item.area.toFixed(2)} ha</p>
                    <p className="text-[10px] font-mono" style={{ color: 'var(--ink-faint)' }}>{item.plantas.toLocaleString('es-CR')} plantas</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3" style={{ borderColor: 'var(--line)' }}>
              <div>
                <div className="flex items-center gap-2">
                  <Layers3 size={15} style={{ color: 'var(--emp-accent-txt)' }} />
                  <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Cronología de aplicaciones</p>
                </div>
                <p className="mt-0.5 text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                  {boletasVisibles.length} de {boletasEnRango.length} aplicaciones visibles
                  {boletas.some((item) => item.origen === 'VFP') ? ' · incluye histórico VFP' : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {filtroEstado !== 'todos' && (
                  <button type="button" onClick={() => setFiltroEstado('todos')} className="inline-flex items-center gap-1 rounded border px-2.5 py-1.5 text-[10px] font-semibold" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                    <Filter size={11} /> Quitar filtro
                  </button>
                )}
                <button type="button" onClick={() => setOrdenAscendente((value) => !value)} className="inline-flex items-center gap-1 rounded border px-2.5 py-1.5 text-[10px] font-semibold" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                  <ChevronDown size={11} style={{ transform: ordenAscendente ? 'rotate(180deg)' : 'none' }} />
                  {ordenAscendente ? 'Más antiguas primero' : 'Más recientes primero'}
                </button>
              </div>
            </div>

            {boletasVisibles.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
                <Clock3 size={30} style={{ color: 'var(--ink-faint)', opacity: 0.45 }} />
                <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No hay aplicaciones para este filtro.</p>
              </div>
            ) : (
              <div className="px-3 py-4 sm:px-5">
                {boletasVisibles.slice(0, 250).map((boleta, index) => {
                  const statusKey = estadoVisual(boleta.estado);
                  const status = ESTADOS[statusKey];
                  const prog = boleta.idNumerico == null ? undefined : progreso.get(boleta.idNumerico);
                  const fecha = fechaIsoEvento(boleta, prog);
                  return (
                    <div key={boleta.id} className="grid grid-cols-[66px_22px_minmax(0,1fr)] gap-2 sm:grid-cols-[105px_28px_minmax(0,1fr)] sm:gap-3">
                      <div className="pt-2 text-right">
                        <div className="text-[11px] font-bold sm:text-xs" style={{ color: 'var(--ink)' }}>{fmtFecha(fecha)}</div>
                        {boleta.semana && <div className="mt-0.5 text-[9px]" style={{ color: 'var(--ink-faint)' }}>Sem. {boleta.semana}</div>}
                      </div>
                      <div className="relative flex justify-center">
                        {index < boletasVisibles.slice(0, 250).length - 1 && <div className="absolute bottom-0 top-5 w-px" style={{ background: 'var(--line)' }} />}
                        <div className="relative z-[1] mt-2 h-3 w-3 rounded-full border-2" style={{ background: status.color, borderColor: 'var(--surface-raised)', boxShadow: `0 0 0 2px ${status.border}` }} />
                      </div>
                      <article className="mb-4 min-w-0 rounded-lg border p-3 sm:p-4" style={{ borderColor: status.border, background: status.bg }}>
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-bold" style={{ color: 'var(--ink)' }}>{boleta.etapa || 'Aplicación sin etapa'}</span>
                              {boleta.ciclo && <span className="rounded px-1.5 py-0.5 text-[9px] font-bold" style={{ background: 'var(--surface-overlay)', color: 'var(--ink-muted)' }}>{boleta.ciclo}</span>}
                            </div>
                            <p className="mt-1 text-[10px] sm:text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                              Boleta #{boleta.numero} · {boleta.origen}
                              {boleta.bloques ? ` · BLK ${boleta.bloques}` : ''}
                            </p>
                          </div>
                          <span className="shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide" style={{ borderColor: status.border, background: 'var(--surface-raised)', color: status.color }}>
                            {status.label}
                          </span>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] sm:text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                          {boleta.areaHa != null && <span><b style={{ color: 'var(--ink)' }}>{boleta.areaHa.toFixed(2)}</b> ha</span>}
                          {boleta.plantas != null && <span><b style={{ color: 'var(--ink)' }}>{boleta.plantas.toLocaleString('es-CR')}</b> plantas</span>}
                          {prog && prog.total > 0 && <span><b style={{ color: 'var(--ink)' }}>{Math.min(prog.finalizados, prog.total)}/{prog.total}</b> booms terminados</span>}
                          {boleta.fechaPlan && <span>Plan: <b style={{ color: 'var(--ink)' }}>{fmtFecha(boleta.fechaPlan)}</b></span>}
                        </div>
                        <FlujoAplicacion boleta={boleta} progreso={prog} />
                      </article>
                    </div>
                  );
                })}
                {boletasVisibles.length > 250 && (
                  <p className="py-3 text-center text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                    Se muestran las primeras 250 aplicaciones. Acortá el período para ver un tramo específico.
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="rounded-lg border px-4 py-14 text-center" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }}>
          Seleccioná una finca y un lote para construir la línea de tiempo.
        </div>
      )}
    </div>
  );
}

export default LineaTiempoLotePage;
